<div class="max-w-[80vw]">
    <form [formGroup]="form">
        <!-- Header -->
        <div
            class="flex flex-0 items-center justify-between h-16 pr-3 sm:pr-5 pl-6 sm:pl-8 bg-primary text-on-primary"
            style="margin: -24px"
        >
            <div class="text-lg font-medium">
                {{ location ? "Edit" : "Add" }} Part Location
            </div>
            <button mat-icon-button type="button" (click)="close()" [tabIndex]="-1">
                <mat-icon
                    class="text-current"
                    [svgIcon]="'heroicons_outline:x-mark'"
                ></mat-icon>
            </button>
        </div>

        <div class="grid sm:grid-cols-4 gap-x-6 gap-y-4 w-full mt-12">
            <div class="col-span-4">
                <mat-form-field
                    class="w-full fuse-mat-dense"
                    [subscriptSizing]="'dynamic'"
                >
                    <mat-label>Location Type</mat-label>
                    <mat-select
                        formControlName="locationType"
                        placeholder="Select Type..."
                        (selectionChange)="locationTypeChanged($event)"
                    >
                        @for (option of locationTypes; track option.value) {
                        <mat-option [value]="option.value">{{
                            option.name
                        }}</mat-option>
                        }
                    </mat-select>
                </mat-form-field>
            </div>

            <div class="col-span-4">
                <mat-form-field
                    class="w-full fuse-mat-dense"
                    [subscriptSizing]="'dynamic'"
                >
                    <mat-label>Parent Location</mat-label>
                    <mat-select
                        formControlName="parentLocationId"
                        [required]="requiresParentId()"
                        [disabled]="!requiresParentId()"
                        placeholder="Select Parent..."
                    >
                        @for (option of parentLocations; track option.partLocationId) {
                        <mat-option [value]="option.partLocationId">{{
                            option.code
                        }}</mat-option>
                        }
                    </mat-select>
                </mat-form-field>
            </div>

            <div class="col-span-2 row-span-3">
                <!-- 50mm x 30mm landscape preview: text left, QR right -->
                <div
                    id="qr-print-area"
                    class="qr-border border-solid border-black border-4 rounded-2xl bg-white flex flex-row items-stretch overflow-hidden w-[320px] max-w-full"
                    style="aspect-ratio: 50 / 30"
                >
                    <!-- Part numbers (max 4); a single-part bin adds the part's description and the note.
                         The location code is intentionally not shown. -->
                    <div
                        class="flex-1 min-w-0 flex flex-col items-center justify-center text-center p-3 gap-0.5 text-black overflow-hidden"
                    >
                        @for (pn of getLabelPartNumbers(); track pn) {
                        <div class="font-bold text-base leading-tight break-words">
                            {{ pn }}
                        </div>
                        } @if (getLabelDescription()) {
                        <div class="text-xs leading-tight break-words line-clamp-3">
                            {{ getLabelDescription() }}
                        </div>
                        } @if (showLabelNote()) {
                        <div class="font-bold text-base leading-tight break-words">
                            {{ form.controls["labelText"].value }}
                        </div>
                        }
                    </div>
                    <!-- QR still encodes the full location path. The location's Name
                         sits directly under the code (DLM request) so the label reads
                         the same way whichever way up it is stuck on. -->
                    <div
                        class="flex-none flex flex-col items-center justify-center p-1 mr-3 gap-0.5"
                    >
                        <qrcode
                            [qrdata]="
                                'nexus-part-location-' + getFullLocationPath()
                            "
                            [width]="136"
                            [errorCorrectionLevel]="'M'"
                            [elementType]="'svg'"
                            allowEmptyString
                            class="block [&>svg]:block"
                        ></qrcode>
                        @if (form.controls["name"].value) {
                        <div
                            class="font-bold text-sm leading-tight text-black text-center break-words max-w-[136px]"
                        >
                            {{ form.controls["name"].value }}
                        </div>
                        }
                    </div>
                </div>
                <button
                    mat-stroked-button
                    class="w-full mt-2"
                    (click)="printQrCode()"
                    type="button"
                >
                    <mat-icon
                        svgIcon="heroicons_outline:printer"
                        class="icon-size-4"
                    ></mat-icon>
                    <span class="ml-1">Print Label</span>
                </button>
            </div>

            <!-- Code is hidden for Bins — it's auto-set from the first part
                 number. Other location types still set it manually. -->
            @if (!isBin()) {
            <div class="col-span-2">
                <mat-form-field
                    class="w-full fuse-mat-dense"
                    [subscriptSizing]="'dynamic'"
                >
                    <mat-label>Code</mat-label>
                    <input
                        formControlName="code"
                        matInput
                        placeholder="e.g. E, F, G"
                    />
                </mat-form-field>
            </div>
            }
            <div class="col-span-2">
                <mat-form-field
                    class="w-full fuse-mat-dense"
                    [subscriptSizing]="'dynamic'"
                >
                    <mat-label>Name</mat-label>
                    <input
                        formControlName="name"
                        matInput
                        placeholder="e.g. Packaging Materials"
                    />
                </mat-form-field>
            </div>
            <div class="col-span-2">
                <mat-form-field
                    class="w-full fuse-mat-dense"
                    [subscriptSizing]="'dynamic'"
                >
                    <mat-label>Capacity</mat-label>
                    <input
                        formControlName="capacity"
                        type="number"
                        matInput
                        [placeholder]="form.get('locationType')?.value === PartLocationType.Bin ? 'Max units of stock' : 'Max bins/items'"
                    />
                </mat-form-field>
            </div>
            <div class="col-span-2">
                <mat-form-field
                    class="w-full fuse-mat-dense"
                    [subscriptSizing]="'dynamic'"
                >
                    <mat-label>Label Text</mat-label>
                    <input
                        formControlName="labelText"
                        matInput
                        [maxlength]="labelTextMaxLength"
                        placeholder="Free text printed on the label"
                    />
                    <mat-hint align="end">
                        {{ form.controls["labelText"].value?.length || 0 }}/{{
                            labelTextMaxLength
                        }}
                    </mat-hint>
                </mat-form-field>
            </div>

            <div class="col-span-4">
                <mat-form-field
                    class="w-full fuse-mat-dense"
                    [subscriptSizing]="'dynamic'"
                >
                    <mat-label>Notes</mat-label>
                    <textarea
                        formControlName="notes"
                        placeholder="Temperature controlled, ATEX zone, hazardous materials, etc."
                        matInput
                        [rows]="2"
                        cdkTextareaAutosize
                        [cdkAutosizeMinRows]="2"
                    >
                    </textarea>
                </mat-form-field>
            </div>

            <!-- Workshop flag: marks this location as a calibration-workshop
                 shelf/bin (Form 10a) rather than warehouse stock, so the two
                 pools stay separate in their respective pickers. -->
            <div class="col-span-4 flex items-center gap-3">
                <mat-slide-toggle
                    formControlName="isWorkshop"
                    color="primary"
                ></mat-slide-toggle>
                <span>Workshop location (calibration / Form 10a — not warehouse stock)</span>
            </div>

            <!-- Parts stored in this bin. A bin can hold many parts; search by
                 part number and keep adding. Bin-level locations only. -->
            @if (isBin()) {
            <div class="col-span-4">
                <mat-form-field
                    class="w-full fuse-mat-dense"
                    [subscriptSizing]="'dynamic'"
                >
                    <mat-label>Parts in this Bin</mat-label>
                    <mat-icon
                        matPrefix
                        class="icon-size-5 ml-1 mr-2"
                        [svgIcon]="'heroicons_outline:magnifying-glass'"
                    ></mat-icon>
                    <input
                        matInput
                        [formControl]="partSearchCtrl"
                        [matAutocomplete]="partAuto"
                        placeholder="Search part number to add..."
                    />
                    <mat-autocomplete
                        #partAuto="matAutocomplete"
                        [displayWith]="partDisplayWith"
                        (optionSelected)="onPartSelected($event)"
                    >
                        @for (option of partOptions; track option.partId) {
                        <mat-option [value]="option">
                            <span class="font-medium">{{ option.number }}</span>
                            @if (option.description) {
                            <span class="text-gray-500 dark:text-gray-400 ml-2">{{
                                option.description
                            }}</span>
                            }
                        </mat-option>
                        }
                    </mat-autocomplete>
                </mat-form-field>

                @if (addedParts.length) {
                <div class="flex flex-wrap gap-2 mt-3">
                    @for (part of addedParts; track part.partId) {
                    <div
                        class="flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full bg-primary-50 text-primary-700 border border-primary-200"
                    >
                        <span class="font-medium text-sm">{{
                            part.number
                        }}</span>
                        <button
                            mat-icon-button
                            type="button"
                            class="icon-size-5 min-h-5 h-5 w-5"
                            matTooltip="Remove part"
                            (click)="removePart(part.partId)"
                        >
                            <mat-icon
                                class="icon-size-4"
                                [svgIcon]="'heroicons_outline:x-mark'"
                            ></mat-icon>
                        </button>
                    </div>
                    }
                </div>
                } @else {
                <div class="text-gray-500 dark:text-gray-400 text-sm mt-2">
                    No parts assigned to this bin yet.
                </div>
                }
            </div>
            }
        </div>

        <!-- Divider -->
        <div class="mt-11 mb-10 border-t"></div>

        <!-- Actions -->
        <div class="flex flex-row items-between w-full gap-4">
            <div class="flex items-center justify-end ml-auto">
                <button (click)="close()" mat-stroked-button type="button">
                    Cancel
                </button>

                <button
                    (click)="save()"
                    class="ml-4"
                    mat-flat-button
                    type="button"
                    [color]="'primary'"
                    [disabled]="!form?.valid || form?.pristine"
                >
                    {{ location ? "Save" : "Create" }}
                </button>
            </div>
        </div>
    </form>
</div>
